/*
 * This is a wrapper for the top level Insights. It loads an Insight dynamically
 * based on the passed in slug and parameters.
 */
import React, { useEffect } from 'react'

import { isEqual } from 'lodash'

import { ConfigurableCard, Alert } from '@/components/ui'
import { useApi, useCanSelectDataSource } from '@/hooks'
import { withApiTiming } from '@/insights/common/withApiTiming'
import { InsightConfigPanel } from '@/insights/components/InsightConfigPanel'
import InsightFullscreen from '@/insights/components/InsightFullscreen'
import {
  type DashboardType,
  type InsightConfigurationType,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatus,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import {
  DATA_SOURCE_KEY,
  DATA_SOURCE_VALUES,
  isTimescaleReady,
  TIMESCALE_UNAVAILABLE_MESSAGE,
} from '@/types/data-source'
import logger from '@/utils/logger'

const log = logger({ category: 'Insight' })

interface InsightParams<P extends InsightParametersType, D> {
  dashboard: DashboardType
  configuration: InsightConfigurationType
  definition: InsightDefinition<P, D>
  editMode?: boolean
  isFullscreen?: boolean
  onFullscreenChange?: (fs: boolean) => void
}

const Insight = <P extends InsightParametersType, D>(props: InsightParams<P, D>) => {
  const { definition, configuration, editMode, isFullscreen: isFullscreenProp, onFullscreenChange } = props
  const { api } = useApi()
  type DataType = Exclude<Awaited<ReturnType<typeof definition.load>>, InsightStatus>
  // type ParamsOf<T> = T extends InsightDefinition<infer P, D> ? P : never
  // type ParamsType = ParamsOf<typeof definition>
  const [result, setResult] = React.useState<{ data?: DataType; status: InsightStatus }>({
    data: undefined,
    status: { code: InsightStatusCode.STARTING },
  })
  // What the last completed load cost, shown in the footer so a Fresh and a Timescale copy
  // of the same insight can be compared directly.
  const canSelectDataSource = useCanSelectDataSource()
  const [lastLoad, setLastLoad] = React.useState<{ at: Date; apiMs: number; calls: number }>()
  const insightKey = JSON.stringify(configuration.parameters)
  const [refresh, setRefresh] = React.useState(0)
  const [configuring, setConfiguring] = React.useState(false)
  const [csvExportLoading, setCsvExportLoading] = React.useState(false)
  const [localFullscreen, setLocalFullscreen] = React.useState(false)
  const fullscreen = onFullscreenChange ? (isFullscreenProp ?? false) : localFullscreen
  const setFullscreen = onFullscreenChange ? (fs: boolean) => onFullscreenChange(fs) : setLocalFullscreen

  function shouldRefreshToday(parameters: InsightParametersType): boolean {
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    console.debug('shouldRefreshToday - checking parameters:', {
      date: parameters.date,
      activeSince: parameters.activeSince,
      from: parameters.from,
      to: parameters.to,
    })

    if (parameters.date === 'custom' && parameters.activeSince) {
      const activeSince = new Date(parameters.activeSince as string)
      activeSince.setHours(0, 0, 0, 0)
      const shouldRefresh = activeSince <= now

      return shouldRefresh
    }

    if (parameters.from || parameters.to) {
      const from = parameters.from ? new Date(parameters.from as string) : new Date(0)
      const to = parameters.to ? new Date(parameters.to as string) : new Date(9999, 11, 31)
      from.setHours(0, 0, 0, 0)
      to.setHours(23, 59, 59, 999)
      const shouldRefresh = from <= now && now <= to

      return shouldRefresh
    }

    if (parameters.date !== undefined) {
      const dateOption = parameters.date as string
      const includesToday = ['', 'today', 'thisWeek', 'lastHour', 'previous1Month']
      const shouldRefresh = includesToday.includes(dateOption)
      return shouldRefresh
    }

    return true
  }

  // Have to autofill these at run-time so they change as the user views different entities
  const parameters = React.useMemo(() => {
    const dashboardParams = props.dashboard?.parameters || {}
    const insightParams = configuration.parameters as P
    const merged = { ...insightParams }
    // Dashboard-level params override insight-level params when set
    if (dashboardParams.companyId) (merged as Record<string, unknown>).companyId = dashboardParams.companyId
    if (dashboardParams.assetId) (merged as Record<string, unknown>).assetId = dashboardParams.assetId
    if (dashboardParams.date) (merged as Record<string, unknown>).date = dashboardParams.date
    if (dashboardParams.from) (merged as Record<string, unknown>).from = dashboardParams.from
    if (dashboardParams.to) (merged as Record<string, unknown>).to = dashboardParams.to
    return merged as P
  }, [definition, props.dashboard?.parameters, configuration.parameters])

  const handleExportCsv = React.useCallback(async () => {
    if (definition?.csv) {
      setCsvExportLoading(true)
      try {
        await definition.csv(api, parameters, configuration.title)
      } catch (ex) {
        log.error('CSV export failed %o', ex)
        // Could show a toast notification here
      } finally {
        // Add a minimum delay to show the spinner even for fast exports
        setTimeout(() => {
          setCsvExportLoading(false)
        }, 1000)
      }
    }
  }, [definition, parameters, configuration.title])

  console.log({ refresh })
  React.useEffect(() => {
    console.log('in ue: %o', { refresh, parameters, api: !!api })
    if (!api) {
      return
    }
    // Refuse rather than quietly serving Fresh. The top-level URL of an unconverted insight
    // is an alias that runs the Fresh code, so asking for Timescale would return Fresh data
    // and two panels side by side would look like proven parity off one database.
    const requested = (parameters as Record<string, unknown>)[DATA_SOURCE_KEY]
    if (requested === DATA_SOURCE_VALUES.timescale && !isTimescaleReady(definition?.slug)) {
      setResult({
        data: undefined,
        status: { code: InsightStatusCode.ERROR, message: TIMESCALE_UNAVAILABLE_MESSAGE },
      })
      return
    }

    const isValid = definition?.configuration.isValid(parameters)
    if (isValid === true) {
      console.log('isvalid')
      setResult((previous) => ({ ...previous, status: { code: InsightStatusCode.LOADING } }))
      const fn = async () => {
        const startTime = Date.now()
        try {
          console.log('loading')
          const { api: timedApi, timing } = withApiTiming(api)
          const response = await definition.load(timedApi, parameters)
          setLastLoad({ at: new Date(), apiMs: timing.totalMs, calls: timing.calls })

          // calc fetch time
          const elapsed = Date.now() - startTime
          const minimumDelay = 300

          if (elapsed < minimumDelay) {
            await new Promise((resolve) => setTimeout(resolve, minimumDelay - elapsed))
          }
          if (isInsightStatus(response)) {
            setResult({ data: undefined, status: response })
          } else {
            if (result.data && isEqual(result.data, response)) {
              // what here?
              setResult((previous) => ({
                ...previous,
                status: { code: InsightStatusCode.DISPLAYING },
              }))
            } else {
              setResult({ data: response as DataType, status: { code: InsightStatusCode.DISPLAYING } })
            }
          }
        } catch (ex) {
          log.error('catch %o', ex)
          setResult({ data: undefined, status: { code: InsightStatusCode.ERROR, message: (ex as Error).message } })
        }
      }
      fn()
    } else {
      console.log('not isvalid')
      setResult({
        data: undefined,
        status: {
          code: InsightStatusCode.NOT_CONFIGURED,
          message: isValid === false ? '' : isValid,
        },
      })
    }
  }, [api, parameters, refresh])

  useEffect(() => {
    if (!definition?.refreshInterval) {
      return
    }

    const shouldRefresh = shouldRefreshToday(configuration.parameters)

    if (!shouldRefresh) {
      return
    }

    const intervalId = setInterval(() => {
      setRefresh((old) => old + 1)
    }, definition.refreshInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [definition?.refreshInterval, configuration.parameters])

  const onClose = (insightId?: string) => {
    if (insightId) {
      // Scroll into view and highlight
      setTimeout(() => {
        const el = document.getElementById(insightId.replace(/\//g, '-'))
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
          const border = el.style.border
          Object.assign(el.style, { transition: 'border-color 2s', border: '2px solid yellow' })
          setTimeout(() => (el.style.border = border), 2000)
        }
      }, 400)
    }
    setConfiguring(false)
  }

  const config = { ...configuration }
  config.parameters = parameters as InsightParametersType
  if (!definition) {
    return null
  }

  // key attribute in these is to force refresh just like the load
  // Caption
  const caption = definition?.Caption ? (
    <definition.Caption key={insightKey} configuration={config} status={result.status} data={result.data} />
  ) : null

  // Content
  const content = definition ? (
    <definition.Display key={insightKey} configuration={config} status={result.status} data={result.data} />
  ) : null

  // Footer
  const count = Array.isArray(result.data) ? result.data.length : null
  // Timed from the insight's own API calls, so the two back ends can be compared. Reads the
  // completed load rather than the current time, which the previous `new Date()` here did not
  // — it re-evaluated on every render and so never showed when the data actually arrived.
  // The timing is only meaningful to someone comparing the two back ends, so it rides behind
  // the same permission as the picker. Last Update stays visible to everyone — insights with
  // their own footer already showed it, and gating it would take away something they had.
  const loadStats = lastLoad ? (
    <span className="text-xs">
      {' '}
      Last Update: {lastLoad.at.toLocaleTimeString()}
      {canSelectDataSource
        ? ` · API ${String(Math.round(lastLoad.apiMs))} ms${lastLoad.calls > 1 ? ` (${String(lastLoad.calls)} calls)` : ''}`
        : ''}
    </span>
  ) : null

  const footer = definition?.Footer ? (
    <div>
      <definition.Footer key={insightKey} configuration={configuration} status={result.status} data={result.data} />
      {loadStats}
    </div>
  ) : (
    <div>
      {count && (
        <span>
          {count} row{count === 1 ? '' : 's'}
        </span>
      )}
      {loadStats}
    </div>
  )

  const badInsight = !definition && result.status.code !== InsightStatusCode.LOADING
  // TODO: make sure there isn't some other data format where this doesn't work
  const hasData = (result.data as [])?.length > 0
  const isRefreshingValue = result.status.code === InsightStatusCode.LOADING
  const errorMessage = (result.status.code === InsightStatusCode.ERROR && result.status.message) || ''
  const displayContent = badInsight ? <Alert message="Invalid Insight" type="error" /> : content

  return (
    <>
      <InsightConfigPanel isOpen={configuring} configuration={config} onClose={onClose} />
      {fullscreen && (
        <InsightFullscreen
          title={config.title}
          caption={caption}
          content={displayContent}
          footer={footer}
          error={errorMessage}
          onClose={() => setFullscreen(false)}
          onConfigure={() => setConfiguring(true)}
          onRefresh={() => setRefresh((old) => old + 1)}
          onExportCsv={definition?.csv && hasData ? handleExportCsv : undefined}
          csvExportLoading={csvExportLoading}
        />
      )}
      <ConfigurableCard
        id={config.id.replace(/\//g, '-')}
        loading={result.status.code === InsightStatusCode.LOADING && !content}
        error={errorMessage}
        title={config.title}
        caption={caption}
        content={displayContent}
        footer={footer}
        onConfigure={() => setConfiguring(true)}
        onRefresh={() => setRefresh((old) => old + 1)}
        onExportCsv={definition?.csv && hasData ? handleExportCsv : undefined}
        csvExportLoading={csvExportLoading}
        inEditMode={editMode}
        isRefreshing={isRefreshingValue}
        onFullscreen={() => setFullscreen(true)}
      />
    </>
  )
}

export default Insight
